// CTV-785: choose the broadcast-quality file that a Finish actually produced.
//
// Waymark's `videoRendered` payload (and getVideoData) carries every render
// the video ever had: email and broadcast formats, old and new, succeeded and
// still in progress. A naive "first succeeded broadcast_quality entry" picks
// the PREVIOUS file on a re-render, which made a reviewed commercial look
// finished before its new render existed (and kept the old file). Rules:
//   1. Only succeeded broadcast_quality renders with a URL qualify.
//   2. Newest `renderedAt` wins. Entries without a timestamp sort after the
//      timestamped ones, later entries first (Waymark appends).
//   3. A render at `previousUrl` (the file we already hold) is skipped while
//      any other candidate exists.
//   4. If every candidate is the previous URL, it counts only when it was
//      re-rendered at or after `since` (the Finish that started this wait),
//      which covers a re-render that overwrote the same URL in place.

export interface RenderLike {
  format: string;
  url: string;
  status: string;
  renderedAt?: string | null;
}

export interface PickRenderOptions {
  /** The broadcast file already in hand before this Finish, if any. */
  previousUrl?: string | null;
  /** Epoch ms of the Finish that started this wait, if known. */
  since?: number | null;
}

function renderedAtMs(r: RenderLike): number | null {
  if (!r.renderedAt) return null;
  const ms = Date.parse(r.renderedAt);
  return Number.isFinite(ms) ? ms : null;
}

export function pickBroadcastRender<R extends RenderLike>(
  renders: ReadonlyArray<R> | null | undefined,
  opts: PickRenderOptions = {},
): R | null {
  const candidates = (renders ?? [])
    .map((r, i) => ({ r, i, ms: renderedAtMs(r) }))
    .filter(({ r }) => r.status === 'succeeded' && r.format === 'broadcast_quality' && !!r.url)
    .sort((a, b) => {
      if (a.ms === null && b.ms === null) return b.i - a.i;
      if (a.ms === null) return 1;
      if (b.ms === null) return -1;
      return b.ms - a.ms;
    });
  if (candidates.length === 0) return null;

  const previousUrl = opts.previousUrl ?? null;
  const fresh = previousUrl ? candidates.filter((c) => c.r.url !== previousUrl) : candidates;
  if (fresh.length > 0) return fresh[0].r;

  const top = candidates[0];
  if (opts.since != null && top.ms !== null && top.ms >= opts.since) return top.r;
  return null;
}
